import { Box, Typography, Chip, Divider, LinearProgress, useTheme } from "@mui/material";
import MetricCard from "../../common/MetricCard";
import type { DeflectionsDashboardResponse } from "../../../types/deflections";

interface Props {
  internal: NonNullable<DeflectionsDashboardResponse["internal"]>;
}

const STATUS_LABELS: Record<string, string> = {
  matched: "Matched",
  not_found: "Not found",
  multiple_matches: "Multiple matches",
  no_transaction_source: "No transaction source",
  unmapped_merchant: "Unmapped merchant",
  missing_merchant_info: "Missing merchant info",
  error: "Error",
  unknown: "Unknown",
};

function humanize(key: string): string {
  return STATUS_LABELS[key] ?? key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

export default function DeflectionsInternalSection({ internal }: Props) {
  const theme = useTheme();
  const { deflectionOutcomes, matchAccuracy, responses } = internal;

  const statusColor = (key: string): string => {
    switch (key) {
      case "matched":
        return theme.palette.success.main;
      case "no_transaction_source":
        return theme.palette.primary.main;
      case "error":
        return theme.palette.error.main;
      case "not_found":
        return theme.palette.warning.main;
      case "multiple_matches":
        return theme.palette.info.main;
      case "unmapped_merchant":
        return theme.palette.secondary.main;
      case "missing_merchant_info":
        return theme.palette.grey[500];
      default:
        return theme.palette.grey[400];
    }
  };

  const entries = Object.entries(matchAccuracy.breakdown).sort((a, b) => b[1] - a[1]);
  const total = matchAccuracy.total || entries.reduce((sum, [, count]) => sum + count, 0);
  const pctOf = (count: number) => (total > 0 ? Math.round((count / total) * 1000) / 10 : 0);

  return (
    <Box>
      <Divider sx={{ mb: 3 }} />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Stats
        </Typography>
        <Chip
          label="Internal use only"
          size="small"
          variant="outlined"
          sx={{ height: 20, fontSize: "0.7rem", color: "text.secondary", borderColor: "divider" }}
        />
      </Box>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-6">
        <MetricCard label="Deflection Success Rate" value={`${deflectionOutcomes.successRate}%`} />
        <MetricCard label="Failed Deflections" value={deflectionOutcomes.failed.toLocaleString()} />
        <MetricCard label="Timed-out Deflections" value={deflectionOutcomes.timeout.toLocaleString()} />
        <MetricCard
          label="Transaction Match Accuracy"
          value={`${matchAccuracy.rate}% (${matchAccuracy.matched.toLocaleString()}/${matchAccuracy.total.toLocaleString()})`}
        />
        <MetricCard
          label="Response Rate"
          value={`${responses.rate}% (${responses.responded.toLocaleString()}/${responses.total.toLocaleString()})`}
        />
      </div>

      {entries.length > 0 && total > 0 && (
        <Box
          sx={{
            mt: 3,
            p: 3,
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: "background.paper",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 2 }}
          >
            Match breakdown
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {entries.map(([status, count]) => (
              <Box
                key={status}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(110px, 180px) 1fr auto",
                  alignItems: "center",
                  columnGap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <Box
                    sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: statusColor(status), flexShrink: 0 }}
                  />
                  <Typography variant="body2" noWrap>
                    {humanize(status)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={pctOf(count)}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "action.hover",
                    "& .MuiLinearProgress-bar": { bgcolor: statusColor(status), borderRadius: 4 },
                  }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "right", whiteSpace: "nowrap", minWidth: 88 }}
                >
                  <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                    {count.toLocaleString()}
                  </Box>{" "}
                  {pctOf(count)}%
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
